import React, { FunctionComponent, useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { theme } from 'config';

import Select, { components } from 'react-select';
import FormErrors from '../FormError';

export type SelectionOption = {
  value: string;
  label: string;
};

type DropDown = {
  options: SelectionOption[];
  label: {
    children: string;
  };
  placeholder: string;
};

type Props = {
  name: string;
  mccRemovalArrayName?: string;
  dropDownOptions: DropDown;
  label: string;
  onSelectChange?: Function;
  defaultValues?: SelectionOption[];
};

const MultiValueRemove = (props: any) => (
  <components.MultiValueRemove {...props}>
    <i className="delete icon" />
  </components.MultiValueRemove>
);

const customStyles = {
  multiValue: (_: any, state: any) => {
    return {
      color: '#646464 !important',
      fontSize: '.875rem !important',
      padding: '.25rem !important',
      backgroundColor: theme.softViolet,
      borderBottomLeftRadius: '.25rem',
      borderBottomRightRadius: '.25rem',
      borderTopLeftRadius: '.25rem',
      borderTopRightRadius: '.25rem',
      margin: '.25rem',
      display: 'flex',
      maxWidth: '96%',
    };
  },
  multiValueLabel: () => ({
    marginLeft: '.25rem',
  }),
  multiValueRemove: (styles: any) => ({
    ...styles,
    color: '#938EAF',
    height: '200%',
    marginBottom: '.178rem',
    ':hover': {
      backgroundColor: theme.softViolet,
      color: '#000',
    },
  }),
};

const cache = createCache({
  key: 'jss-class-prefix-key',
  nonce:
    (document.querySelector('meta[property="csp-nonce"]') as HTMLMetaElement)
      .content ?? '',
});

const FormSelectMCCList: FunctionComponent<Props> = ({
  label,
  name,
  mccRemovalArrayName,
  dropDownOptions,
  defaultValues = null,
}) => {
  const defaultSelectionOptions = defaultValues ? defaultValues : [];
  const [internalSelectionOptions, setInternalSelectionOptions] = useState<
    SelectionOption[]
  >(defaultSelectionOptions);
  const { errors, setValue } = useFormContext();

  useEffect(() => {
    setInternalSelectionOptions(defaultSelectionOptions);
    setValue(name, defaultValues);
  }, []);

  const handleChange = (data: any) => {
    // update mcc to add
    const mccUpdated = data ? data.map((d: SelectionOption) => d.value) : [];
    setValue(name, mccUpdated);

    // set mcc values to remove
    if (mccRemovalArrayName && defaultValues) {
      const mccToDelete = internalSelectionOptions
        .filter((mcc: SelectionOption) => !mccUpdated.includes(mcc.value))
        .map((mccOptions) => mccOptions.value);
      setValue(mccRemovalArrayName, mccToDelete);
    }
  };

  return (
    <CacheProvider value={cache}>
      <div>
        <label htmlFor={name}>
          <span>{label}</span>
          <Select
            name={name}
            components={{ MultiValueRemove, IndicatorSeparator: () => null }}
            placeholder={dropDownOptions.placeholder}
            options={dropDownOptions.options}
            onChange={handleChange}
            isMulti
            defaultValue={defaultValues}
            styles={customStyles}
            isClearable={false}
          />
        </label>
        {errors && <FormErrors name={name} errors={errors} />}
      </div>
    </CacheProvider>
  );
};

export default FormSelectMCCList;
